import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, Switch, TextField, Typography, CircularProgress,
} from '@mui/material';
import { collection, doc, getDoc, getDocs, getFirestore, updateDoc, serverTimestamp } from 'firebase/firestore';
import firebaseApp from '../../../utils/firebaseConfig';
import { getCurrentIdentity } from '../../../protectedRoute';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const formatPrice = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

// Control de la membresía RECURRENTE desde la cuenta del usuario (pedido
// 2026-10-05: "si el cliente no sabe hacerlo y viene a la tienda"). Antes
// solo se podía apagar desde la app o procesando una solicitud de cancelación.
// autoRenewMemberships cobra members.membershipPlanId a
// members.renewalPaymentMethodId al vencer, así que aquí se puede ver y
// cambiar exactamente eso.
export default function MemberRenewalCard({ memberId }) {
  const [member, setMember] = useState(null);
  const [plans, setPlans] = useState([]);
  const [card, setCard] = useState(null); // { brand, last4 } | null
  const [cardState, setCardState] = useState('idle');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [planOpen, setPlanOpen] = useState(false);
  const [planId, setPlanId] = useState('');

  const load = useCallback(async () => {
    if (!memberId) return;
    try {
      const [m, p] = await Promise.all([
        getDoc(doc(db, 'members', memberId)),
        getDocs(collection(db, 'membership_plans')),
      ]);
      setMember(m.exists() ? { id: m.id, ...m.data() } : null);
      setPlans(p.docs.map((d) => ({ id: d.id, ...d.data() })).filter((x) => x.active !== false));
    } catch (e) { setError(e.message); }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  // Últimos 4 de la tarjeta guardada para la renovación
  useEffect(() => {
    if (!member || !member.renewalPaymentMethodId || !member.renewalStripeCustomerId) { setCard(null); return undefined; }
    let alive = true;
    setCardState('loading');
    axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: member.renewalStripeCustomerId })
      .then((r) => {
        if (!alive) return;
        const pm = (r.data.paymentMethods || []).find((x) => x.id === member.renewalPaymentMethodId);
        setCard(pm && pm.card ? { brand: pm.card.brand, last4: pm.card.last4 } : null);
        setCardState(pm ? 'ok' : 'missing');
      })
      .catch(() => { if (alive) setCardState('error'); });
    return () => { alive = false; };
  }, [member]);

  const write = async (data) => {
    setSaving(true); setError('');
    try {
      await updateDoc(doc(db, 'members', memberId), { ...data, updatedAt: serverTimestamp() });
      await load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const toggle = async (on) => {
    if (on && !(member.renewalPaymentMethodId && member.renewalStripeCustomerId)) {
      setError('No hay tarjeta guardada para renovar. El cliente debe comprar/renovar una vez desde la tienda o la app para guardar la tarjeta.');
      return;
    }
    if (!on && !window.confirm('¿Apagar la renovación automática? La membresía seguirá activa hasta su fecha de vencimiento y después NO se cobrará.')) return;
    await write({
      autoRenew: on,
      autoRenewDisabledReason: on ? null : `staff:${getCurrentIdentity().displayName || 'staff'}`,
      ...(on ? { autoRenewAttempts: 0, autoRenewLastError: null } : {}),
    });
  };

  const savePlan = async () => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    setPlanOpen(false);
    await write({ membershipPlanId: plan.id, membershipPlanName: plan.name || '', planName: plan.name || '' });
  };

  if (!memberId) return null;
  if (!member) return null;

  const plan = plans.find((p) => p.id === member.membershipPlanId);
  const exp = toDate(member.expiration);
  const price = plan ? (Number(plan.renewalPriceCents) > 0 ? plan.renewalPriceCents : plan.priceCents) : null;
  const hasCard = Boolean(member.renewalPaymentMethodId && member.renewalStripeCustomerId);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Membresía recurrente</Typography>
          <Chip size="small" color={member.autoRenew ? 'success' : 'default'} label={member.autoRenew ? 'Renovación automática ON' : 'Renovación automática OFF'} />
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        <Stack spacing={0.5} mb={2}>
          <Row k="Plan que se renueva" v={plan ? plan.name : (member.membershipPlanName || member.planName || 'Sin plan asignado')} />
          <Row k="Próximo cobro" v={member.autoRenew && exp ? `${exp.toLocaleDateString('es-PR')}${price ? ` · ${formatPrice(price)} + IVU` : ''}` : (exp ? `No se cobrará (vence ${exp.toLocaleDateString('es-PR')})` : '—')} />
          <Row k="Tarjeta guardada" v={
            !hasCard ? 'Ninguna' : cardState === 'loading' ? <CircularProgress size={14} /> : card ? `${(card.brand || '').toUpperCase()} •••• ${card.last4}` : cardState === 'missing' ? 'La tarjeta ya no existe en Stripe' : 'Tarjeta guardada'
          } />
          {member.autoRenewLastError && <Alert severity="warning" sx={{ mt: 1 }}>Último intento falló: {member.autoRenewLastError} ({member.autoRenewAttempts || 0} intento(s))</Alert>}
          {member.autoRenewDisabledReason && !member.autoRenew && <Typography variant="caption" color="text.secondary">Apagada por: {member.autoRenewDisabledReason === 'customer_app' ? 'el cliente desde la app' : member.autoRenewDisabledReason === 'cancelled_by_customer' ? 'solicitud de cancelación' : member.autoRenewDisabledReason}</Typography>}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel control={<Switch checked={Boolean(member.autoRenew)} onChange={(e) => toggle(e.target.checked)} disabled={saving} />} label={member.autoRenew ? 'Cancelar renovación' : 'Activar renovación'} />
          <Button size="small" variant="outlined" onClick={() => { setPlanId(member.membershipPlanId || ''); setPlanOpen(true); }} disabled={saving}>Cambiar plan</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Apagar la renovación no cancela la membresía vigente ni reembolsa. Para desactivar la cuenta usa el switch Active de arriba.
        </Typography>
      </CardContent>

      <Dialog open={planOpen} onClose={() => setPlanOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Plan que se renovará</DialogTitle>
        <DialogContent dividers>
          <TextField select fullWidth label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)} sx={{ mt: 1 }}>
            {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name} — {formatPrice(Number(p.renewalPriceCents) > 0 ? p.renewalPriceCents : p.priceCents)}{Number(p.renewalPriceCents) > 0 ? ' (renovación)' : ''}</MenuItem>)}
          </TextField>
          <Alert severity="info" sx={{ mt: 2 }}>Solo cambia qué plan se cobra en la próxima renovación. No cobra nada ahora ni cambia la fecha de vencimiento.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePlan} disabled={!planId}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function Row({ k, v }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{k}</Typography>
      <Typography variant="body2" component="div" sx={{ textAlign: 'right' }}>{v}</Typography>
    </Stack>
  );
}
Row.propTypes = { k: PropTypes.string, v: PropTypes.node };

MemberRenewalCard.propTypes = { memberId: PropTypes.string };
